import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { TabbedOverviewPage } from '../components/OverviewSections.comp';
import { RubricCard, ThresholdTable } from '../../SchoolDashboard/tabs/AboutRubrics.tab';
import { useDashboard } from '../../../context/DashboardContext';

const SectionHeader = ({ title, subtitle }) => (
    <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '23px', fontWeight: 800, color: '#052F44', lineHeight: 1.25 }}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{ fontSize: '14px', fontStyle: 'italic', color: '#6B7280', mt: 0.75, lineHeight: 1.6 }}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

// `text` may be a single string (one flowing sentence) or an array of
// distinct items — arrays render one per line instead of running together.
const NoteBanner = ({ label, text }) => (
    <Box sx={{
        mt: 4, p: { xs: 2, md: 2.5 }, borderRadius: '10px',
        backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', border: '1px solid var(--brand-border, #E5E7EB)',
        borderLeft: '4px solid #052F44',
    }}>
        {label && <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#052F44', mb: 0.5 }}>{label}</Typography>}
        {Array.isArray(text) ? (
            text.map((line, i) => (
                <Typography key={i} sx={{ fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>{line}</Typography>
            ))
        ) : (
            <Typography sx={{ fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>{text}</Typography>
        )}
    </Box>
);

// Fallback content — no backend section S_10 exists yet for National, so
// this page previously rendered entirely blank (an early `return null`).
// Reproduced verbatim from the Dashboard Common Section deck, shared with
// every other dashboard's identical "How the Assessment Is Scored" page.
const FALLBACK_HEADLINE_CARDS = [
    {
        headline_code: 'A',
        headline_title: 'Children Online Safety Readiness',
        headline_html: '<p>Preparedness to act safely, responsibly, and effectively online.</p><table>'
            + '<tr><td>High</td><td>Strong, consistent readiness to participate online safely, responsibly, and effectively.</td></tr>'
            + '<tr><td>Moderate</td><td>Generally adequate readiness, with some areas for reinforcement.</td></tr>'
            + '<tr><td>Basic</td><td>Partial or inconsistent readiness; targeted support may be needed.</td></tr>'
            + '<tr><td>Low</td><td>Limited readiness or risky gaps requiring priority attention.</td></tr>'
            + '</table>',
    },
    {
        headline_code: 'B',
        headline_title: 'Child Cyber-Risk Experiences',
        headline_html: '<p>Reported exposure to, involvement in, or impact from cyber risks.</p><table>'
            + '<tr><td>Monitor</td><td>Lower than the reference threshold or minimal cyber-risk signal</td></tr>'
            + '<tr><td>Watch</td><td>Noticeable signal or within expected reference range</td></tr>'
            + '<tr><td>Elevated</td><td>Above typical reference level or meaningful cyber-risk pattern</td></tr>'
            + '<tr><td>Priority Concern</td><td>Top-quartile reference level or high cyber-risk prevalence</td></tr>'
            + '</table>',
    },
    {
        headline_code: 'C',
        headline_title: 'Context & Support',
        headline_html: '<p>Access, guidance, school education, home support, and help-seeking conditions.</p><table>'
            + '<tr><td>Strong</td><td>Strong access, support, guidance, or protective conditions.</td></tr>'
            + '<tr><td>Adequate</td><td>Generally positive conditions, with some minor limitations.</td></tr>'
            + '<tr><td>Limited</td><td>Partial, inconsistent, or uncertain support conditions.</td></tr>'
            + '<tr><td>Low</td><td>Weak, insufficient, or absent support conditions.</td></tr>'
            + '</table>',
    },
    {
        headline_code: 'D',
        headline_title: 'Participation and Data Confidence',
        headline_html: '<p>Assessment coverage and the reliability of results for interpretation.</p><table>'
            + '<tr><td>High</td><td>Strong enough for interpretation</td></tr>'
            + '<tr><td>Medium</td><td>Usable with confidence label</td></tr>'
            + '<tr><td>Low</td><td>Interpret cautiously</td></tr>'
            + '<tr><td>Very Low</td><td>Do not use for strong action or comparison</td></tr>'
            + '</table>',
    },
];

const RISK_RUBRIC_COLUMNS = ['#', 'Risk Category', 'Risk indicator', 'Banding method', 'Monitor', 'Watch', 'Elevated', 'Priority Concern'];
const RISK_RUBRIC_ROWS = [
    ['1', 'Headline', 'Any Cyber Risk', 'Reference percentile', '≤71.95%', '>71.95–76.62%', '>76.62–80.09%', '>80.09%'],
    ['2', 'Conduct Risk', 'Cyberbullying', 'Reference percentile', '≤26.29%', '>26.29–31.70%', '>31.70–34.71%', '>34.71%'],
    ['3', 'Conduct Risk', 'Cybervictimization', 'Reference percentile', '≤32.73%', '>32.73–39.53%', '>39.53–44.86%', '>44.86%'],
    ['4', 'Conduct Risk', 'AI Image and Voice Misuse Victimization', 'Provisional AI-risk', '0–4.9%', '5–14.9%', '15–29.9%', '≥30%'],
    ['5', 'Content Risk', 'Sexual Content Exposure', 'Reference percentile', '≤20.67%', '>20.67–25.99%', '>25.99–31.02%', '>31.02%'],
    ['6', 'Content Risk', 'Violent Content Exposure', 'Reference percentile', '≤15.75%', '>15.75–18.19%', '>18.19–20.38%', '>20.38%'],
    ['7', 'Content Risk', 'AI Misinformation and Deceptive Content Risk', 'Provisional AI-risk', '0–4.9%', '5–14.9%', '15–29.9%', '≥30%'],
    ['8', 'Contact Risk', 'Offline Meetings with Online Strangers', 'Reference percentile', '≤23.20%', '>23.20–26.30%', '>26.30–30.05%', '>30.05%'],
    ['9', 'Contact Risk', 'Sexual Conversation', 'Reference percentile', '≤7.85%', '>7.85–9.43%', '>9.43–12.93%', '>12.93%'],
    ['10', 'Contact Risk', 'Unwanted Online Sexual Contact', 'Reference percentile', '≤19.41%', '>19.41–27.59%', '>27.59–31.73%', '>31.73%'],
    ['11', 'Contact Risk', 'AI-Generated Sexual Image Risk', 'Provisional safeguarding-sensitive AI-risk', '0–0.9%', '1–4.9%', '5–9.9%', '≥10%'],
    ['12', 'Contract & Data Risk', 'Misuse of Personal Information', 'Reference percentile', '≤19.19%', '>19.19–25.87%', '>25.87–30.57%', '>30.57%'],
    ['13', 'Contract & Data Risk', 'Underage Technology Use', 'Reference percentile', '≤39.54%', '>39.54–50.02%', '>50.02–58.02%', '>58.02%'],
    ['14', 'Wellbeing & Screen Use Risk', 'Excessive Screen Time', 'Reference percentile', '≤47.92%', '>47.92–55.12%', '>55.12–60.71%', '>60.71%'],
    ['15', 'Wellbeing & Screen Use Risk', 'Video Game Addiction Risk', 'Reference percentile', '≤8.23%', '>8.23–10.08%', '>10.08–13.33%', '>13.33%'],
    ['16', 'Wellbeing & Screen Use Risk', 'Online Technology Addiction Risk', 'Reference percentile', '≤34.39%', '>34.39–39.01%', '>39.01–49.01%', '>49.01%'],
    ['17', 'Wellbeing & Screen Use Risk', 'AI Emotional Reliance', 'Provisional AI-risk', '0–4.9%', '5–14.9%', '15–29.9%', '≥30%'],
];
const FALLBACK_TABLE_CARD = {
    title: 'Risk Rubric',
    table_objects: RISK_RUBRIC_COLUMNS.map((column_name, colIdx) => ({
        column_name,
        values: RISK_RUBRIC_ROWS.map(row => row[colIdx]),
    })),
};

const METHODOLOGY_SECTION_CODE = 'S_10';
const CARD_SECTIONS = [METHODOLOGY_SECTION_CODE];

const MethodologyContent = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    // Defensively locate section S_10
    let s10Section = null;
    if (dashboardData) {
        if (dashboardData.section_code === METHODOLOGY_SECTION_CODE) {
            s10Section = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s10Section = dashboardData.cards.sections.find(s => s.section_code === METHODOLOGY_SECTION_CODE);
        } else if (Array.isArray(dashboardData.sections)) {
            s10Section = dashboardData.sections.find(s => s.section_code === METHODOLOGY_SECTION_CODE);
        }
    }

    if (loading) {
        return null;
    }

    const cards = s10Section?.cards || [];

    const titleCard = cards.find(c => c.card_code === 'C_1');
    const headlineCards = cards.filter(c => c.card_type === 'HeadlineCard');
    const leftLineCard = cards.find(c => c.card_type === 'LeftLineCard');

    // Risk Rubric Title comes from C_7 TitleCard
    const c7Card = cards.find(c => c.card_code === 'C_7');
    // TableCard comes from C_9
    const tableCard = cards.find(c => c.card_code === 'C_9');
    // ListCard comes from C_8
    const listCard = cards.find(c => c.card_code === 'C_8');

    const displayHeadlineCards = headlineCards.length > 0 ? headlineCards : FALLBACK_HEADLINE_CARDS;

    const displayTitle = titleCard?.title || "How the COS Assessment Is Scored";
    const displaySubtitle = titleCard?.footer || "What was measured, and how should it be interpreted?";

    // Extract text from LeftLineCard C_6 (Decision Guidance) — split on
    // paragraph/line-break boundaries so each point still gets its own line
    // instead of collapsing into one run-on paragraph once tags are stripped.
    let decisionText = [
        'Readiness scores describe student preparedness.',
        'Risk scores describe reported risk experience.',
        'Context scores describe conditions around the child, not the child’s competency.',
    ];
    if (leftLineCard?.leftline_html) {
        decisionText = leftLineCard.leftline_html
            .replace('Decision Guidance:', '')
            .split(/<\/p>|<br\s*\/?>/i)
            .map(part => part.replace(/<[^>]*>/g, '').trim())
            .filter(Boolean);
    }

    // Merge C_7 title into tableCard title for ThresholdTable rendering —
    // fall back to the deck's own 17-row Risk Rubric when no backend
    // TableCard exists yet.
    const thresholdTableCard = tableCard ? {
        ...tableCard,
        title: c7Card?.title || tableCard.title || 'Risk Rubric'
    } : FALLBACK_TABLE_CARD;
    const displayListCard = tableCard ? listCard : null;

    return (
        <Box>
            <SectionHeader
                title={displayTitle}
                subtitle={displaySubtitle}
            />

            <Grid container spacing={3} alignItems="stretch">
                {displayHeadlineCards.map((card, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={card.card_code || idx}>
                        <RubricCard card={card} />
                    </Grid>
                ))}
            </Grid>

            <ThresholdTable card={thresholdTableCard} listCard={displayListCard} />

            <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mt: 3, lineHeight: 1.6 }}>
                * For established risks, bands use risk-specific reference thresholds. For AI-related risks, provisional thresholds are used until enough reference data exists.
            </Typography>
        </Box>
    );
};

const MethodologyPage = () => (
    <TabbedOverviewPage
        skipNoDataGate
        tabs={[
            {
                label: 'How the Assessment Is Scored',
                content: <MethodologyContent />,
            },
        ]}
    />
);

export default MethodologyPage;
